import type { StateCreator } from 'zustand';
import type { Deck } from '../../types';
import type { StoreState } from '../useStore';
import * as dataApi from '../../services/dataApi';
import { normalizeDeck } from '../normalizers';
import { reportError } from '@/services/errorTracking';

export interface DecksSlice {
  decks: Deck[];
  setDecks: (decks: Deck[]) => void;
  addDeck: (deck: Omit<Deck, 'id' | 'createdAt'>) => Promise<void>;
  updateDeck: (id: string, updates: Partial<Deck>) => Promise<void>;
  deleteDeck: (id: string) => Promise<void>;
}

export const createDecksSlice: StateCreator<StoreState, [], [], DecksSlice> = (set) => ({
  decks: [],
  setDecks: (decks) => set({ decks }),

  addDeck: async (deck) => {
    try {
      const res = (await dataApi.createDeck(deck)) as Record<string, unknown>;
      const newDeck = normalizeDeck(res);
      set((state) => ({ decks: [...state.decks, newDeck] }));
    } catch (err) {
      reportError(err, { operation: 'addDeck' });
      throw err;
    }
  },

  updateDeck: async (id, updates) => {
    try {
      const res = (await dataApi.updateDeck(id, updates)) as Record<string, unknown>;
      const updated = normalizeDeck(res);
      set((state) => ({
        decks: state.decks.map((d) => (d.id === id ? updated : d)),
      }));
    } catch (err) {
      reportError(err, { operation: 'updateDeck' });
      throw err;
    }
  },

  deleteDeck: async (id) => {
    try {
      await dataApi.deleteDeck(id);
      set((state) => ({
        decks: state.decks.filter((d) => d.id !== id),
        cards: state.cards.filter((c) => c.deckId !== id),
      }));
    } catch (err) {
      reportError(err, { operation: 'deleteDeck' });
      throw err;
    }
  },
});
